// RBAC capability helpers for the React layer (POC — preset menus).
//
// The backend ships the signed-in user's capabilities into the AppRouter
// props (see app/views/preset_menus/index.rb#serialize_current_user). The FE
// uses them only to hide actions the user can't perform — the server policy
// (PresetMenuPolicy) remains the source of truth and rejects forbidden writes
// with a 403 regardless of what the UI shows.

export interface CurrentUser {
  id: number;
  role: string;
  // Optional: absent when the user isn't signed in or the page didn't
  // serialize it. Treated as "no capabilities" → fail closed.
  capabilities?: string[];
  // email is always present when currentUser is; name may be blank (see
  // User#name validation, allow_blank: true) and is omitted in that case.
  // Consumed by AppShell's PostHogUserIdentifier for person identification.
  email?: string;
  name?: string;
}

// The FE-side capability registry — the mirror of the backend's
// `Capabilities::ALL` (app/policies/capabilities.rb). Listing every capability
// the React layer checks in one typed const means `can()` only accepts a known
// capability: a typo'd or renamed capability is a compile error here, the same
// way an unknown capability raises in `User#can?` on the backend. Add a new
// entry whenever a controller's resource gains FE gating.
export const CAPABILITIES = {
  CAPACITIES_READ: 'capacities.read',
  CAPACITIES_WRITE: 'capacities.write',
  CONTACTS_READ: 'contacts.read',
  CONTACTS_WRITE: 'contacts.write',
  EVENTS_READ: 'events.read',
  EVENTS_WRITE: 'events.write',
  EVENT_ORDERS_GENERATE: 'event_orders.generate',
  INVENTORIES_READ: 'inventories.read',
  INVENTORIES_WRITE: 'inventories.write',
  OPERATING_HOURS_READ: 'operating_hours.read',
  OPERATING_HOURS_WRITE: 'operating_hours.write',
  ORDERS_READ: 'orders.read',
  ORDERS_WRITE: 'orders.write',
  ORDER_SOURCES_READ: 'order_sources.read',
  ORDER_SOURCES_WRITE: 'order_sources.write',
  PORTAL_APPS_READ: 'portal_apps.read',
  PRESET_MENUS_READ: 'preset_menus.read',
  PRESET_MENUS_WRITE: 'preset_menus.write',
  PRICE_MODIFIERS_READ: 'price_modifiers.read',
  PRICE_MODIFIERS_WRITE: 'price_modifiers.write',
  LOCATIONS_READ: 'locations.read',
  LOCATIONS_WRITE: 'locations.write',
  USERS_READ: 'users.read',
  USERS_WRITE: 'users.write',
  USER_ROLES_WRITE: 'user_roles.write',
  DEV_TOOLS_WRITE: 'dev_tools.write',
  PRODUCTS_READ: 'products.read',
  PRODUCTS_WRITE: 'products.write',
  BUNDLES_READ: 'bundles.read',
  BUNDLES_WRITE: 'bundles.write',
  RECIPES_READ: 'recipes.read',
  RECIPES_WRITE: 'recipes.write',
  RESALE_ITEMS_READ: 'resale_items.read',
  RESALE_ITEMS_WRITE: 'resale_items.write',
  ORGANISATIONS_READ: 'organisations.read',
  ORGANISATIONS_WRITE: 'organisations.write',
  SSO_SCOPE_REQUESTS_READ: 'sso_scope_requests.read',
  SSO_SCOPE_REQUESTS_WRITE: 'sso_scope_requests.write',
  SSO_AUDIT_READ: 'sso_audit.read',
  SCHEDULER_READ: 'scheduler.read',
  SCHEDULER_WRITE: 'scheduler.write',
  LALAMOVE_BOOKINGS_WRITE: 'lalamove_bookings.write',
} as const;

export type Capability = (typeof CAPABILITIES)[keyof typeof CAPABILITIES];

// Does this user hold the given capability (e.g. CAPABILITIES.PRESET_MENUS_WRITE)?
// Undefined user or missing capabilities list → false (fail closed).
export function can(
  user: CurrentUser | undefined,
  capability: Capability,
): boolean {
  return user?.capabilities?.includes(capability) ?? false;
}
